import React from 'react';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import {
  useNavReconOverview,
  useNavReconPipelineMetadata,
} from '../../../../api/rs-hg/nav-recon/hooks/queries';
import type {
  NavReconOverview2Response,
  NavReconPipelineMetadata,
} from '../../../../api/rs-hg/nav-recon/types';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import NavReconOverviewTable from './components/NavReconOverviewTable';

/** Object payload so QueryBoundary does not treat an empty overview list as missing data. */
type NavReconOverviewLoaded = {
  overview: NavReconOverview2Response;
  pipelineMetadata: NavReconPipelineMetadata[];
};

/**
 * NAV-Recon overview — `NavReconOverviewExpandedObject` / `get_nav_recon_overview2`.
 */
const NavReconOverviewPage: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = ({ positionDate, section }) => {
  const overview = useNavReconOverview(positionDate);
  const pipelineMetadata = useNavReconPipelineMetadata(positionDate);

  const loaded: NavReconOverviewLoaded | undefined =
    overview.data && pipelineMetadata.data
      ? {
        overview: overview.data,
        pipelineMetadata: pipelineMetadata.data,
      }
      : undefined;

  const isOverviewLoading =
    !positionDate ||
    overview.isPending ||
    pipelineMetadata.isPending;

  return (
    <QueryBoundary<NavReconOverviewLoaded>
      isLoading={isOverviewLoading}
      isError={overview.isError || pipelineMetadata.isError}
      error={overview.error ?? pipelineMetadata.error}
      data={loaded}
      loadingMessages={[
        'Loading NAV-Recon overview…',
        'Fetching pipeline metadata…',
      ]}
      errorMessage="An error occurred while loading NAV-Recon overview."
      noDataMessage="No NAV-Recon overview data for the selected date."
    >
      {(data) => (
        <NavReconOverviewTable
          data={data.overview.funds}
          pipelineMetadata={data.pipelineMetadata}
          positionDate={positionDate!}
          section={section}
        />
      )}
    </QueryBoundary>
  );
};

export default NavReconOverviewPage;
